import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Title, Box, Text } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import parseAwardsFormatting from '../../lib/parse-awards-formatting';
import getTextColor from '~/client/lib/text-color-config';
import Disclosure from '../../../disclosure/disclosure';

import HeroSelect from '../../awards-hero-select/awards-hero-select';
import awardCategoriesList from '../../data/2022/award-categories';

import {
  stringSelectSpec,
  stringInputSpec,
  boolCheckboxSpec,
} from '~/client/lib/editing-configs';

import '../../awards-hero.less';

export const getTextAwardsColor = (layout) => {
  switch (layout) {
    case 'ms-2022':
      return 'white';
    case 'lp1-2022':
    case 'lp2-2022':
      return 'green-dark';
    default:
      return 'green';
  }
};

const textSizeEnum = {
  2: 'default',
  3: 'large',
};

const HeroLayout = ({
  layout,
  title,
  mobileTitle,
  size,
  textColorOptions,
  subtitle,
  backgroundIcon,
  animatedIcon,
  animatedIconSmall,
  props,
  categoriesDropdown,
  awardsCategory,
  disclosureType,
  disclosureOnBackground,
  shouldTrack,
}) => {
  const { textColor } = getTextColor(textColorOptions);
  const colorStyle = { color: textColor };
  let icon = animatedIconSmall;
  let displayIcon = false;
  if (size === 'lg') {
    icon = animatedIcon;
    displayIcon = true;
  }
  if (layout === 'lp1-2022') {
    displayIcon = true;
  }
  if (layout !== 'lp2-2022') {
    displayIcon = true;
  }
  const isSmallLayout = size === 'sm' || size === 'md';
  const headingSize = isSmallLayout ? 'small' : 'default';
  const headingValue = isSmallLayout && mobileTitle ? mobileTitle : title;
  const subtitleSize = isSmallLayout
    ? 'small'
    : textSizeEnum[props?.subtitleSize];
  const subtitleValue =
    isSmallLayout && props?.subtitleMobile ? props.subtitleMobile : subtitle;
  const showDisclosure = disclosureType !== 'none' && layout === 'lp2-2022';

  return (
    <React.Fragment>
      <Box
        className={classNames([
          'awards-hero__content-grid-background',
          `${layout}`,
        ])}
      />
      <Box className={classNames(['awards-hero__wrapper', `${layout}`])}>
        <Box className={classNames(['awards-hero__content-grid', `${layout}`])}>
          <Box className={classNames(['awards-hero__background', `${layout}`])}>
            <Image
              src={backgroundIcon}
              alt="Best Of Awards 2022"
              backgroundColor="transparent"
              className="awards-hero__background-icon"
            />
          </Box>
          {layout === 'lp2-2022' && (
            <Box className="awards-hero__grid-overlay">
              <div
                className={classNames([
                  'awards-hero__animated-icon',
                  `${layout}`,
                ])}
                style={{
                  backgroundImage: `url(${icon})`,
                }}
              />
            </Box>
          )}
        </Box>
        <Box className={classNames(['awards-hero__content', `${layout}`])}>
          {showDisclosure && (
            <div
              className={classNames([
                'awards-hero__advertiser-disclosure',
                `${layout}`,
              ])}
            >
              <Disclosure
                type={disclosureType}
                onBackground={disclosureOnBackground}
              />
            </div>
          )}
          <Box
            className={classNames([
              'awards-hero__title',
              `${layout}`,
              { 'awards-select': categoriesDropdown },
            ])}
            style={colorStyle}
          >
            <Title component="h1" size={headingSize}>
              {parseAwardsFormatting(headingValue)}
            </Title>
          </Box>
          {categoriesDropdown && (
            <HeroSelect
              selected={awardsCategory}
              colorStyle={colorStyle}
              layout={layout}
              size={size}
              headingSize={headingSize}
              shouldTrack={shouldTrack}
            />
          )}
          <Box
            className={classNames(['awards-hero__subtitle', `${layout}`])}
            style={colorStyle}
          >
            <Text size={subtitleSize}>{subtitleValue}</Text>
          </Box>
          {displayIcon && (
            <div
              className={classNames([
                'awards-hero__animated-icon',
                `${layout}`,
              ])}
              style={{
                backgroundImage: `url(${icon})`,
              }}
            />
          )}
        </Box>
      </Box>
    </React.Fragment>
  );
};

HeroLayout.propTypes = {
  layout: PropTypes.string,
  title: PropTypes.string,
  mobileTitle: PropTypes.string,
  size: PropTypes.string,
  textColor: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleSize: PropTypes.string,
  subtitleTextClass: PropTypes.string,
  backgroundIcon: PropTypes.string,
  animatedIcon: PropTypes.string,
  animatedIconSmall: PropTypes.string,
  textColorOptions: PropTypes.string,
  subtitleMobile: PropTypes.string,
  awardsCategory: PropTypes.string,
  categoriesDropdown: PropTypes.bool,
  props: PropTypes.object,
  disclosureType: PropTypes.string,
  disclosureOnBackground: PropTypes.bool,
  shouldTrack: PropTypes.bool,
};

HeroLayout.defaultProps = {
  layout: '2022MS',
  awardsCategory: 'credit-cards',
};

HeroLayout.getEditingConfig = (componentDocument) => {
  const animatedImageConfig =
    componentDocument?.props?.awardsHeroType === '2022LP2'
      ? [
          stringInputSpec('animatedIcon', 'Right Side Image (Desktop)'),
          stringInputSpec('animatedIconSmall', 'Right Side Image (Mobile)'),
        ]
      : [
          stringInputSpec('animatedIcon', 'Animated Icon (Desktop)'),
          stringInputSpec('animatedIconSmall', 'Animated Icon (Mobile)'),
        ];

  const awardsDropdownOptions = componentDocument?.props?.categoriesDropdown
    ? [
        stringSelectSpec(
          'awardsCategory',
          'Award Categories',
          awardCategoriesList
        ),
      ]
    : [];

  const lp2OnlyConfig =
    componentDocument?.props?.awardsHeroType === '2022LP2'
      ? [
          ...Disclosure.getEditingConfig(),
          boolCheckboxSpec(
            'shouldTrack',
            'Add BOA Awards analytics instrumentation?'
          ),
          boolCheckboxSpec(
            'categoriesDropdown',
            'Show the Award Categories Dropdown?'
          ),
        ]
      : [];
  return [
    stringSelectSpec('awardsHeroType', '2022 Awards Hero Type', [
      { label: '2022 Microsite', value: '2022MS' },
      { label: '2022 LP1', value: '2022LP1' },
      { label: '2022 LP2', value: '2022LP2' },
    ]),
    ...animatedImageConfig,
    ...lp2OnlyConfig,
    ...awardsDropdownOptions,
    stringInputSpec('backgroundIcon', 'Side icon'),
  ];
};

export default HeroLayout;
